import type {
  CompletionRateData,
  CompletionRatePayload,
} from '@api/survey_dashboard/utilities/helpers';
import {
  Accordion,
  Card,
  ChartPieSliceIcon,
  LoaderSkeleton,
  TableData,
  UserCheckIcon,
} from '@utilities';

import styles from './_index.module.scss';

export interface CompletionRateCardProps {
  data: CompletionRatePayload | null;
  isLoading: boolean;
}

const CompletionRateCard = ({ data, isLoading }: CompletionRateCardProps) => {
  const tableData = data?.data?.[0]?.table;
  const columns = tableData?.columns ?? [];
  const rows = tableData?.data ?? [];

  if (isLoading) {
    return (
      <div className={styles['completion-rate-card-skeleton']} data-testid="completion-rate-card">
        <LoaderSkeleton height={40} width={1000}>
          <rect height="40" rx="4" ry="4" width="1000" x="0" y="0" />
        </LoaderSkeleton>
      </div>
    );
  }

  return (
    <Card
      as={Accordion}
      className={styles['completion-rate-card']}
      data-testid="completion-rate-card"
      label={<Header data={tableData?.data} />}
    >
      {columns.length > 0 && (
        <TableData columns={columns} data={rows} isLoading={isLoading} {...tableData} />
      )}
    </Card>
  );
};

const Header = ({ data }: { data?: CompletionRateData[] }) => {
  const completedResponseCount = data?.reduce((acc, item) => acc + (item?.completes ?? 0), 0) ?? 0;
  const disqualifiedCount = data?.reduce((acc, item) => acc + (item?.disqualified ?? 0), 0) ?? 0;
  const totalResponseCount = completedResponseCount + disqualifiedCount;
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    style: 'percent',
  });
  const disqualificationRate = formatter.format(disqualifiedCount / (totalResponseCount || 1));

  return (
    <div className={styles['completion-rate-card-header']}>
      <div className={styles['completion-rate-card-header-content']}>
        <ChartPieSliceIcon />
        <strong>Completed Responses</strong> <span>{completedResponseCount}</span>
      </div>
      <div className={styles['completion-rate-card-header-content']}>
        <UserCheckIcon />
        <strong>Disqualification Rate</strong> <span>{disqualificationRate}</span>{' '}
        <span className={styles['completion-rate-card-header-completed-response-count']}>
          {`(${disqualifiedCount} out of ${totalResponseCount} total responses)`}
        </span>
      </div>
    </div>
  );
};

export default CompletionRateCard;
